import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

const HERO = "/hero-cnc.jpg";

export default function AuthPage({ mode }) {
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const isLogin = mode === "login";
  useEffect(() => {
    const root = document.documentElement, wasLight = root.classList.contains("light");
    root.classList.remove("light");
    return () => { if (wasLight) root.classList.add("light"); };
  }, []);
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (isLogin) await login(form.email, form.password);
      else {
        if (form.phone.replace(/\D/g, "").length < 10) {
          setError("Informe um telefone com DDD (ex: (11) 99999-9999).");
          return;
        }
        await register(form.name, form.email, form.phone, form.password);
      }
      nav("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const f = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Fresadora CNC cortando MDF" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1115] via-[#0F1115]/60 to-transparent" />
        <div className="absolute inset-x-0 top-0 p-12 fade-up">
          <img src="/logo-la.png" alt="LA Marcenaria" className="h-28 xl:h-32 w-auto" data-testid="hero-logo" />
        </div>
        <div data-testid="hero-text-wrap" className="absolute inset-x-0 bottom-0 flex justify-center p-12 pb-16 fade-up">
          <h1 data-testid="hero-title" style={{ color: "#fff" }} className="max-w-2xl text-center font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08]">
            Monte a lista de peças, marque fitas e furações e gere o plano de corte otimizado com etiquetas
          </h1>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 relative">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid={`${mode}-form`}>
          <div>
            <img src="/logo-la.png" alt="LA Marcenaria" data-testid="mobile-login-logo" className="lg:hidden h-20 w-auto mx-auto mb-6" />
            <h2 className="font-heading text-3xl font-bold tracking-tight">{isLogin ? "Entrar" : "Criar conta"}</h2>
            <p className="text-sm text-[#9CA3AF] mt-1">{isLogin ? "Acesse seus ambientes de corte." : "Cadastre-se para gerar seus planos de corte."}</p>
          </div>
          {!isLogin && (
            <div className="space-y-1.5">
              <Label>Nome</Label>
              <Input data-testid="register-name-input" required {...f("name")} className="bg-[#181B21] border-[#2D333F] h-11" />
            </div>
          )}
          {!isLogin && (
            <div className="space-y-1.5">
              <Label>Telefone para contato</Label>
              <Input data-testid="register-phone-input" type="tel" required placeholder="(11) 99999-9999" {...f("phone")} className="bg-[#181B21] border-[#2D333F] h-11" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>E-mail</Label>
            <Input data-testid={`${mode}-email-input`} type="email" required {...f("email")} className="bg-[#181B21] border-[#2D333F] h-11" />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Senha</Label>
              {isLogin && <Link data-testid="forgot-password-link" to="/recuperar-senha" className="text-xs text-[#E67E22] hover:underline">Esqueci minha senha</Link>}
            </div>
            <Input data-testid={`${mode}-password-input`} type="password" required minLength={isLogin ? 1 : 6} {...f("password")} className="bg-[#181B21] border-[#2D333F] h-11" />
          </div>
          {error && <p data-testid={`${mode}-error`} className="text-sm text-red-400">{error}</p>}
          <Button data-testid={`${mode}-submit-btn`} disabled={busy} className="w-full h-11 bg-[#E67E22] hover:bg-[#D35400] text-black font-semibold">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{isLogin ? "Entrar" : "Criar conta"}
          </Button>
          <p className="text-sm text-[#9CA3AF] text-center">
            {isLogin ? "Ainda não tem conta? " : "Já tem conta? "}
            <Link data-testid={`${mode}-switch-link`} to={isLogin ? "/cadastro" : "/login"} className="text-[#E67E22] hover:underline">
              {isLogin ? "Cadastre-se" : "Entrar"}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
